import cx from 'clsx';
import React from 'react';
import IntersectionObserverWrapper from 'src/components/IntersectionObserverWrapper';
import { IMAGE_SIZES } from 'src/model/algolia/constants';
import { transformImage } from 'src/model/algolia/utils';
import {
  JackpotLayerGameTile,
  LiveTableLayer,
  SashLayer,
} from '../../../components/game-tile';
import { Info } from '../../../components/icons';
import { CategoryFinancialWebAttributes } from '../../../types/category/categoryTypes';
import AssetLayer from '../../AssetLayer/AssetLayer';
import GameTile from '../../game-tile/GameTile';
import LayersWrapper from '../../game-tile/LayersWrapper';
import { QuicksilverTileOutfitType } from '../../game-tile/types';
import GameLink from '../../GameLink';
import { ShouldPreventDefaultForClick } from '../../LaunchStrategiesProvider/utils';
import { INFO_LABEL, SMALL_OUTFIT } from '../constants';
import GhostTile from '../GhostTile';
import { GameClickProps, GameTileProps } from '../types';
import { prepareGameInfoTooltip } from '../utils';

import styles from './ModularGridGameTile.module.scss';

interface ModularGridGameTileProps {
  gameDetailsInfo?: boolean;
  gameInfoTooltip?: string;
  game?: GameTileProps;
  onClick?: (game: GameClickProps) => void;
  onInfoClick?: (game: GameClickProps, path: string) => void;
  outfit: QuicksilverTileOutfitType;
  isLazy?: boolean;
  isAnchor?: boolean;
  ghost?: boolean;
  tileSize?: 'small' | 'large';
  categoryAttributes?: CategoryFinancialWebAttributes;
  renderExtendedInfo?: (game: GameTileProps) => JSX.Element | null;
}

export const prepareImages = (
  gameImage: GameTileProps['image'],
  tileSize: 'small' | 'large'
) => {
  return tileSize === 'small'
    ? IMAGE_SIZES.small.map(config => ({
        min: config.min,
        max: config.max,
        src: transformImage(gameImage.source.base, config.size),
      }))
    : IMAGE_SIZES.large.map(config => ({
        min: config.min,
        max: config.max,
        src: transformImage(gameImage.source.base, config.size),
      }));
};

const handleGameTileClick = (
  e: React.MouseEvent<HTMLAnchorElement>,
  onClick: ModularGridGameTileProps['onClick'],
  game: GameTileProps,
  preventDefaultForGameClick: boolean
) => {
  if (preventDefaultForGameClick) e.preventDefault();
  onClick && onClick(prepareGameClickProps(game));
};

const handleGameTileInfoIconClick = (
  e: React.MouseEvent<HTMLDivElement>,
  onInfoClick: ModularGridGameTileProps['onInfoClick'],
  game: GameTileProps,
  gameDetailsPath: string
) => {
  e.preventDefault();
  onInfoClick && onInfoClick(prepareGameClickProps(game), gameDetailsPath);
};

export const prepareGameClickProps = (game: GameTileProps) => ({
  id: game.id,
  title: game.title,
  name: game.name,
  provider: game.provider,
  integration: game.integration,
  presentation_code: game.presentation_code,
  transaction_code: game.transaction_code,
  launchUrl: game.launchUrl,
  gameDetailsPath: game.gameDetailsPath,
  gameLaunchPath: game.gameLaunchPath,
  type: game.type,
  trackingTitle: game.tracking_metadata?.title || game.title,
  sash: game.sash,
});

const ModularGridGameTile: React.FC<ModularGridGameTileProps> = ({
  game,
  onClick,
  onInfoClick,
  outfit,
  isLazy = false,
  isAnchor = false,
  ghost,
  gameDetailsInfo,
  gameInfoTooltip,
  tileSize = 'small',
  renderExtendedInfo,
  categoryAttributes,
}) => {
  if (ghost) {
    return (
      <div className={styles.ghostTileContainer}>
        <GhostTile />
      </div>
    );
  }
  if (!game) return null;

  const preventDefaultForGameClick = ShouldPreventDefaultForClick(
    game.integration
  );

  const preparedGameInfoTooltip = prepareGameInfoTooltip(
    gameInfoTooltip,
    game.title
  );

  return (
    <IntersectionObserverWrapper
      className={styles.container}
      loadingClassName={styles.loadingWrapper}
      dataTestId="game-tile-wrapper"
    >
      <GameTile data-testid="game-tile">
        <LayersWrapper rounded={renderExtendedInfo ? 'top' : 'all'}>
          <AssetLayer
            type="image"
            sizedSources={
              game.sizedImages
                ? game.sizedImages[
                    tileSize === 'small' ? 'smallTile' : 'largeTile'
                  ]
                : prepareImages(game.image, tileSize)
            }
            loadingAnimation={game.loading}
            data-testid="picture-layer"
            imageSrc={game.image?.source}
            alt={game.image?.alt || ''}
            isLazy={isLazy}
            rounded={renderExtendedInfo ? 'top' : 'all'}
          />
          {game.table && <LiveTableLayer table={game.table} outfit={outfit} />}
          {game.jackpot && !game.table && outfit !== SMALL_OUTFIT && (
            <JackpotLayerGameTile {...game.jackpot} />
          )}
        </LayersWrapper>
        {isAnchor && game.launchUrl && (
          <GameLink
            url={game.launchUrl}
            categoryAttributes={categoryAttributes}
            target="_blank"
            onClick={e =>
              handleGameTileClick(e, onClick, game, preventDefaultForGameClick)
            }
            notIndexed={game.notIndexed}
            integration={game.integration}
          />
        )}
        {gameDetailsInfo && (
          <a
            className={cx(styles.info, styles.link)}
            target="_blank"
            rel="noopener noreferrer"
            href={game.gameDetailsPath}
            title={preparedGameInfoTooltip}
          >
            <div
              className={cx(
                styles.infoWrapper,
                tileSize === 'large' && styles.infoWrapperWithLabel
              )}
              onClick={e =>
                handleGameTileInfoIconClick(
                  e,
                  onInfoClick,
                  game,
                  game.gameDetailsPath
                )
              }
            >
              <Info altText={preparedGameInfoTooltip} />
              {tileSize === 'large' && (
                <p className={styles.infoLabel}>{INFO_LABEL}</p>
              )}
            </div>
          </a>
        )}
        {game.sash && (
          <SashLayer
            sash={game.sash.title}
            outfit={outfit}
            background={game.sash.background}
          />
        )}
      </GameTile>

      {renderExtendedInfo && renderExtendedInfo(game)}
    </IntersectionObserverWrapper>
  );
};

export default ModularGridGameTile;
